import React from 'react';
import PropTypes from 'prop-types';
import { Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaExclamationTriangle, FaInfoCircle, FaQuestionCircle, FaShieldAlt } from 'react-icons/fa';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import s from './ScamRiskBadge.css';

const RISK_META = {
  low: {
    label: 'Low Scam Risk',
    variant: 'secondary',
    icon: FaInfoCircle,
    tooltip: 'No elevated pay-on-delivery scam risk was found.',
  },
  medium: {
    label: 'Potentially Scam',
    variant: 'warning',
    icon: FaExclamationTriangle,
    tooltip: 'This order has scam risk signals and should be reviewed.',
  },
  high: {
    label: 'Huge Scam Risk',
    variant: 'danger',
    icon: FaShieldAlt,
    tooltip: 'This order has strong scam risk signals.',
  },
};

const REVIEW_META = {
  confirmed_scam: {
    label: 'confirmed scam',
    variant: 'danger',
    icon: FaShieldAlt,
    tooltip: 'An admin reviewed this order and confirmed it as scam.',
  },
  false_positive: {
    label: 'false positive',
    variant: 'success',
    icon: FaInfoCircle,
    tooltip: 'An admin reviewed this order and marked it as not scam.',
  },
};

function ScamRiskBadge({ risk, showScore }) {
  const riskLevel = risk && risk.riskLevel;
  const reasons = risk && risk.reasons ? risk.reasons : [];
  const meta = REVIEW_META[risk && risk.reviewStatus] || RISK_META[riskLevel] || {
    label: 'Not checked',
    variant: 'secondary',
    icon: FaQuestionCircle,
    tooltip: 'No scam risk assessment is available for this booking.',
  };
  const Icon = meta.icon;
  const shouldAnimate = risk && risk.reviewStatus === 'pending' && ['medium', 'high'].includes(riskLevel);
  const label = showScore && risk && risk.riskScore != null
    ? `${meta.label} (${risk.riskScore})`
    : meta.label;
  const popover = (
    <Popover id={`scam-risk-${riskLevel || 'none'}`}>
      <Popover.Title as="h3">{meta.label}</Popover.Title>
      <Popover.Content>
        {risk && (
          <div>
            <div><b>Score:</b> {risk.riskScore}</div>
            {risk.trustDeficit != null && <div><b>Trust deficit:</b> {risk.trustDeficit}</div>}
            {risk.exposureBand != null && <div><b>Exposure band:</b> {risk.exposureBand}</div>}
            {risk.reviewStatus && <div><b>Review:</b> {risk.reviewStatus}</div>}
          </div>
        )}
        {!risk && <div>{meta.tooltip}</div>}
        {reasons.length > 0 && (
          <ul className="mt-2 mb-0 pl-3">
            {reasons.map(reason => (
              <li key={`${reason.code}-${reason.points}`}>
                {reason.detail} <b>+{reason.points}</b>
              </li>
            ))}
          </ul>
        )}
      </Popover.Content>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="top"
      overlay={popover}
    >
      <Badge pill variant={meta.variant} className={cx("d-inline-flex align-items-center", { [s.compactBadge]: shouldAnimate })}>
        <Icon className="mr-1" />
        {label}
      </Badge>
    </OverlayTrigger>
  );
}

ScamRiskBadge.propTypes = {
  risk: PropTypes.shape({
    riskLevel: PropTypes.string,
    riskScore: PropTypes.number,
    exposureBand: PropTypes.number,
    trustDeficit: PropTypes.number,
    reviewStatus: PropTypes.string,
    reasons: PropTypes.array,
  }),
  showScore: PropTypes.bool,
};

ScamRiskBadge.defaultProps = {
  risk: null,
  showScore: false,
};

export default withStyles(s)(ScamRiskBadge);
